import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="flex items-start gap-3 group cursor-pointer"
      >
        <div className="w-4 h-4 flex-shrink-0 mt-0.5 flex items-center justify-center">
          <i className="ri-arrow-right-s-line text-primary-400 group-hover:text-primary-600 transition-colors text-xs"></i>
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 font-medium block">
            {article.title}
          </span>
          <p className="text-xs text-foreground-400 mt-0.5">{article.publishedAt}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="flex flex-col sm:flex-row gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200/60 transition-all group cursor-pointer"
      >
        <div className="flex-shrink-0 w-full sm:w-48 h-32 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            title={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-md font-medium"
            >
              {article.category}
            </span>
            {article.isHot && (
              <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-md font-medium flex items-center gap-1">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-fire-fill text-[10px]"></i>
                </div>
                热门
              </span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors text-base line-clamp-2 mb-1.5">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </div>
              {article.views.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-chat-3-line"></i>
              </div>
              {article.comments.toLocaleString()}
            </span>
            <span>{article.publishedAt}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 transition-all group cursor-pointer h-full"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.imageAlt}
          title={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>
      <div className="p-4 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-md font-medium">
            {article.category}
          </span>
          {article.isHot && (
            <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-md font-medium flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-fill text-[10px]"></i>
              </div>
              热门
            </span>
          )}
          {article.isNew && (
            <span className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md font-medium">
              新
            </span>
          )}
        </div>
        <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors text-base line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400 mt-auto">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </div>
            {article.views.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-chat-3-line"></i>
            </div>
            {article.comments.toLocaleString()}
          </span>
          <span>{article.publishedAt}</span>
        </div>
      </div>
    </Link>
  );
}